import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { supabase } from '../../../../lib/supabase';
import { useQuery } from '@tanstack/react-query';
import { supabaseCommunityService } from '../../../../services/supabase/communities';
import type { Community } from '../../../../types/community';

interface AddIncomeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export default function AddIncomeModal({ isOpen, onClose, onSuccess }: AddIncomeModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Fetch communities
  const { 
    data: communities, 
    isLoading: isLoadingCommunities, 
    error: communitiesError 
  } = useQuery<Community[]>({
    queryKey: ['communities'],
    queryFn: supabaseCommunityService.getAll
  });

  const [formData, setFormData] = useState({
    community: '',
    date: '',
    incomeAmount: '',
    description: '',
    acquisitionDate: ''
  });

  // Debug logging
  useEffect(() => {
    if (communitiesError) {
      console.error('Error fetching communities:', communitiesError);
    }
  }, [communitiesError]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      // Create the financial income record
      const { error: insertError } = await supabase
        .from('dev_financials')
        .insert([{
          fin_comm: formData.community,
          fin_date: formData.date || null,
          fin_inc_amt: formData.incomeAmount ? parseFloat(formData.incomeAmount) : null,
          fin_exp_amt: null, // Always null for income records
          fin_desc: formData.description || null,
          fin_exp_proj: null, // Not applicable for income
          fin_acq_date: formData.acquisitionDate || null,
          fin_supplier: null // Not applicable for income
        }]);

      if (insertError) throw insertError;

      onSuccess();
      onClose();
    } catch (err) {
      console.error('Error creating financial income record:', err);
      setError(err instanceof Error ? err.message : 'Failed to create financial income record');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto py-8">
      <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] flex flex-col">
        <div className="flex justify-between items-center p-6 border-b sticky top-0 bg-white z-10">
          <h3 className="text-lg font-medium">Add New Financial Income</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto flex-1">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Community *
            </label>
            {isLoadingCommunities && (
              <div className="mt-1 text-sm text-gray-500">Loading communities...</div>
            )}
            {communitiesError && (
              <div className="mt-1 text-sm text-red-500">
                Error loading communities
              </div>
            )}
            <div className="relative">
              <select
                value={formData.community}
                onChange={(e) => setFormData(prev => ({ ...prev, community: e.target.value }))}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:ring-primary appearance-none bg-white cursor-pointer hover:border-primary"
                required
                disabled={isLoadingCommunities}
                style={{ 
                  backgroundImage: "url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e\")",
                  backgroundPosition: "right 0.5rem center",
                  backgroundRepeat: "no-repeat",
                  backgroundSize: "1.5em 1.5em",
                  paddingRight: "2.5rem"
                }}
              >
                <option value="">Select a Community</option>
                {communities?.map((community) => (
                  <option 
                    key={community.comm_id} 
                    value={community.comm_id}
                    className="py-1"
                    title={community.comm_name}
                  >
                    {community.comm_id}
                  </option>
                ))}
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Date
            </label>
            <input
              type="date"
              value={formData.date}
              onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:ring-primary"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Income Amount
            </label>
            <input
              type="number"
              step="0.01"
              value={formData.incomeAmount}
              onChange={(e) => setFormData(prev => ({ ...prev, incomeAmount: e.target.value }))}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:ring-primary"
              placeholder="Enter income amount"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Description
            </label>
            <input
              type="text"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:ring-primary"
              placeholder="Enter description"
              maxLength={200}
            />
            <p className="mt-1 text-xs text-gray-500">
              {formData.description.length}/200 characters
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Acquittal Date
            </label>
            <input
              type="date"
              value={formData.acquisitionDate}
              onChange={(e) => setFormData(prev => ({ ...prev, acquisitionDate: e.target.value }))}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:ring-primary"
            />
          </div>

          <div className="mt-6 flex justify-end gap-3 sticky bottom-0 pt-4 bg-white">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 bg-green-600 text-white rounded-md text-sm font-medium hover:bg-green-700 disabled:opacity-50"
            >
              {isSubmitting ? 'Creating...' : 'Create Income'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
